import React, { useState, useRef, useEffect } from 'react';
import { useUsers, ROLE_LABELS } from '../context/UsersContext';
import { C } from '../utils/styles';
import Icon from './Icon';
import { exportData, importData } from '../utils/dataSync';

export default function UserProfileDropdown({ onOpenProfile, onOpenChangePassword }) {
  const { currentUser, logout, getInitials, getAvatarColor } = useUsers();
  const [isOpen, setIsOpen] = useState(false);
  const [showImportInput, setShowImportInput] = useState(false);
  const dropdownRef = useRef(null);
  const fileInputRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  if (!currentUser) return null;

  const initials = getInitials(currentUser.firstName, currentUser.lastName);
  const avatarColor = getAvatarColor(currentUser.firstName, currentUser.lastName);

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      {/* Avatar Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '4px 4px 4px 10px',
          borderRadius: 20,
          border: `1px solid ${C.border}`,
          background: C.card,
          cursor: 'pointer',
          transition: 'all 0.15s',
        }}
        onMouseEnter={(e) => e.currentTarget.style.background = C.accentBg}
        onMouseLeave={(e) => e.currentTarget.style.background = C.card}
      >
        <span style={{ fontSize: 12, fontWeight: 600, color: C.text }}>
          {currentUser.firstName}
        </span>
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            background: avatarColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 11,
            fontWeight: 700,
            color: 'white',
          }}
        >
          {initials}
        </div>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            minWidth: 220,
            background: 'white',
            borderRadius: 8,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            border: `1px solid ${C.border}`,
            zIndex: 1000,
            overflow: 'hidden',
          }}
        >
          {/* User Info Header */}
          <div style={{
            padding: 14,
            borderBottom: `1px solid ${C.border}`,
            background: C.bg,
          }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 2 }}>
              {currentUser.firstName} {currentUser.lastName}
            </div>
            <div style={{ fontSize: 11, color: C.textMid, marginBottom: 6 }}>
              {currentUser.email}
            </div>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              padding: '3px 8px',
              background: currentUser.role === 'super_user' ? C.accentBg : C.bg,
              borderRadius: 4,
              fontSize: 10,
              fontWeight: 600,
              color: currentUser.role === 'super_user' ? C.accent : C.textMid,
            }}>
              <Icon
                name={currentUser.role === 'super_user' ? 'key-outline' : 'person-outline'}
                style={{ fontSize: 12 }}
              />
              {ROLE_LABELS[currentUser.role]}
            </div>
          </div>

          {/* Menu Items */}
          <div>
            <button
              onClick={() => {
                setIsOpen(false);
                onOpenProfile();
              }}
              style={{
                width: '100%',
                padding: '10px 14px',
                background: 'transparent',
                border: 'none',
                textAlign: 'left',
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 600,
                color: C.text,
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                transition: 'background 0.15s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = C.accentBg}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <Icon name="person-circle-outline" style={{ fontSize: 16, color: C.textMid }} />
              Edit Profile
            </button>

            <button
              onClick={() => {
                setIsOpen(false);
                onOpenChangePassword();
              }}
              style={{
                width: '100%',
                padding: '10px 14px',
                background: 'transparent',
                border: 'none',
                textAlign: 'left',
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 600,
                color: C.text,
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                transition: 'background 0.15s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = C.accentBg}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <Icon name="lock-closed-outline" style={{ fontSize: 16, color: C.textMid }} />
              Change Password
            </button>

            {/* Saved options (employer-side only) — quick scroll target */}
            {currentUser?.role === 'employer_contact' && (
              <button
                onClick={() => {
                  setIsOpen(false);
                  const el = document.getElementById('saved-options-anchor');
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                data-testid="dropdown-saved-options"
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  background: 'transparent',
                  border: 'none',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: 600,
                  color: C.text,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  transition: 'background 0.15s',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = C.accentBg}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <Icon name="bookmark-outline" style={{ fontSize: 16, color: C.textMid }} />
                Saved options
              </button>
            )}

            <div style={{ borderTop: `1px solid ${C.border}`, margin: '4px 0' }} />
            
            {/* Data Sync Options */}
            <button
              onClick={() => {
                exportData();
                setIsOpen(false);
              }}
              style={{
                width: '100%',
                padding: '10px 14px',
                background: 'transparent',
                border: 'none',
                textAlign: 'left',
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 600,
                color: C.text,
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                transition: 'background 0.15s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = C.accentBg}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <Icon name="download-outline" style={{ fontSize: 16, color: C.textMid }} />
              Export Data
            </button>
            
            <button
              onClick={() => {
                fileInputRef.current?.click();
              }}
              style={{
                width: '100%',
                padding: '10px 14px',
                background: 'transparent',
                border: 'none',
                textAlign: 'left',
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 600,
                color: C.text,
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                transition: 'background 0.15s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = C.accentBg}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <Icon name="cloud-upload-outline" style={{ fontSize: 16, color: C.textMid }} />
              Import Data
            </button>
            
            {/* Hidden file input */}
            <input
              id="import-data-file"
              name="import-file"
              ref={fileInputRef}
              type="file"
              accept=".json"
              style={{ display: 'none' }}
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (file) {
                  try {
                    const result = await importData(file);
                    alert(`Data imported successfully!\n\n${result.usersCount} users\n${result.clientsCount} clients\n\nPlease refresh the page.`);
                    setTimeout(() => window.location.reload(), 1000);
                  } catch (error) {
                    alert(`Import failed: ${error.message}`);
                  }
                }
                e.target.value = ''; // Reset input
                setIsOpen(false);
              }}
            />

            <div style={{ borderTop: `1px solid ${C.border}`, margin: '4px 0' }} />

            <button
              onClick={() => {
                setIsOpen(false);
                logout();
              }}
              style={{
                width: '100%',
                padding: '10px 14px',
                background: 'transparent',
                border: 'none',
                textAlign: 'left',
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 600,
                color: C.red,
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                transition: 'background 0.15s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = C.redLight}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <Icon name="log-out-outline" style={{ fontSize: 16 }} />
              Logout
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
